import * as Sentry from '@sentry/react';

const COMPANION_API_URL = process.env.REACT_APP_COMPANION_API_URL;

function detectBrowser(userAgent) {
  if (/Edg\//.test(userAgent)) return 'Edge';
  if (/Chrome\//.test(userAgent) && !/Chromium/.test(userAgent)) return 'Chrome';
  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) return 'Safari';
  if (/Firefox\//.test(userAgent)) return 'Firefox';
  return 'Unknown';
}

function detectOs(userAgent) {
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Mac OS X|Macintosh/i.test(userAgent)) return 'macOS';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
  if (/Linux/i.test(userAgent)) return 'Linux';
  return 'Unknown';
}

function detectDeviceType(userAgent) {
  if (/iPad|Tablet/i.test(userAgent)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/i.test(userAgent)) return 'mobile';
  return 'desktop';
}

function deviceInfo() {
  const userAgent = navigator.userAgent || '';
  return {
    userAgent,
    browser: detectBrowser(userAgent),
    os: detectOs(userAgent),
    deviceType: detectDeviceType(userAgent),
    language: navigator.language,
    platform: navigator.platform,
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
      pixelRatio: window.devicePixelRatio,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

async function getLocationIfAlreadyGranted() {
  if (!navigator.geolocation || !navigator.permissions?.query) {
    return { location: null, locationPermission: 'not_requested' };
  }

  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    if (permission.state !== 'granted') {
      return { location: null, locationPermission: permission.state };
    }

    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            locationPermission: 'granted',
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
            },
          });
        },
        () => {
          resolve({ location: null, locationPermission: 'unavailable' });
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 24 * 60 * 60 * 1000 }
      );
    });
  } catch {
    return { location: null, locationPermission: 'not_requested' };
  }
}

function nameFor(user, fallback = '') {
  const details = user?.details || {};
  return [details.name, details.surname].filter(Boolean).join(' ').trim() || fallback;
}

export async function trackClientUsageEvent({ eventType, user, metadata }) {
  if (!COMPANION_API_URL || !user?.id) return;

  try {
    const { location, locationPermission } = await getLocationIfAlreadyGranted();
    const companies = [...(user.companiesCanEdit || []), ...(user.companiesManaging || [])];

    await fetch(`${COMPANION_API_URL}/api/usage/client-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType,
        userId: user.id,
        role: user.role || 'client',
        source: 'cp-redesign',
        userName: nameFor(user),
        email: user.details?.email || metadata?.email || '',
        companyIds: companies.map((company) => company.id).filter(Boolean),
        companyNames: companies.map((company) => company.name).filter(Boolean),
        device: deviceInfo(),
        location,
        locationPermission,
        metadata,
      }),
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'usageIntelligence', event_type: 'USAGE_INTELLIGENCE_FAILED' },
      extra: { eventType, userId: user?.id },
    });
  }
}
